import { useEffect, useState, useMemo, type CSSProperties, type FormEvent } from "react";
import {
  getInventoryKPIs,
  getInventoryUsage,
  getInventoryCountHistory,
  startInventoryCount,
  getCountItems,
  recordItemCount,
  completeInventoryCount,
  getCountVariance,
  type InventoryKPIs,
  type InventoryUsageItem,
  type InventoryCountHistoryRow,
  type CountItemRow,
  type CountVarianceRow,
} from "../../lib/api";
import WhiskeyStudioTabs from "../../components/admin/WhiskeyStudioTabs";
import BarcodeScannerModal from "../../components/common/BarcodeScannerModal";

function fmtCurrency(cents: number | null | undefined): string {
  if (cents == null) return "—";
  return "$" + ((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(dStr: string | null | undefined): string {
  if (!dStr) return "—";
  return new Date(dStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const VELOCITY_COLORS: Record<string, { bg: string; color: string }> = {
  fast: { bg: "rgba(39,174,96,0.15)", color: "#27ae60" },
  moderate: { bg: "rgba(249,175,54,0.15)", color: "var(--gold)" },
  slow: { bg: "rgba(230,126,34,0.15)", color: "#e67e22" },
  dead: { bg: "rgba(138,122,102,0.15)", color: "var(--muted)" },
};

export default function AdminInventoryCount() {
  const [activeTab, setActiveTab] = useState<"usage" | "history" | "active_count" | "variance">("usage");
  const [loading, setLoading] = useState(true);

  // Usage Intelligence State
  const [kpis, setKpis] = useState<InventoryKPIs | null>(null);
  const [usageItems, setUsageItems] = useState<InventoryUsageItem[]>([]);
  const [usageSearch, setUsageSearch] = useState("");
  const [usageVelocityFilter, setUsageVelocityFilter] = useState("all");
  const [usageTypeFilter, setUsageTypeFilter] = useState("all");
  const [usageSort, setUsageSort] = useState("units_30d");

  // Count History & Sessions
  const [history, setHistory] = useState<InventoryCountHistoryRow[]>([]);
  const [isStartModalOpen, setIsStartModalOpen] = useState(false);
  const [countType, setCountType] = useState("full");
  const [countNotes, setCountNotes] = useState("");
  const [startingCount, setStartingCount] = useState(false);

  // Active Count Session
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [countItems, setCountItems] = useState<CountItemRow[]>([]);
  const [countSearch, setCountSearch] = useState("");
  const [countTypeFilter, setCountTypeFilter] = useState<"all" | "bottle" | "pour">("all");
  const [showOnlyUncounted, setShowOnlyUncounted] = useState(false);
  const [completingCount, setCompletingCount] = useState(false);

  // Variance View
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [varianceRows, setVarianceRows] = useState<CountVarianceRow[]>([]);
  const [loadingVariance, setLoadingVariance] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ text: string; type: "ok" | "err" } | null>(null);
  const showToast = (text: string, type: "ok" | "err" = "ok") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Escape key closes modals
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isStartModalOpen) setIsStartModalOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isStartModalOpen]);

  // Load Usage & KPIs
  const loadUsageData = async () => {
    setLoading(true);
    try {
      const [kpiRes, usageRes] = await Promise.all([
        getInventoryKPIs().catch(() => null),
        getInventoryUsage({
          item_type: usageTypeFilter !== "all" ? usageTypeFilter : null,
          velocity: usageVelocityFilter !== "all" ? usageVelocityFilter : null,
          search: usageSearch.trim() || null,
          sort: usageSort,
          limit: 100,
        }),
      ]);
      if (kpiRes) setKpis(kpiRes);
      setUsageItems(usageRes || []);
    } catch (err: any) {
      showToast(err.message || "Failed to load usage data", "err");
    } finally {
      setLoading(false);
    }
  };

  // Load Count History
  const loadHistoryData = async () => {
    setLoading(true);
    try {
      const rows = await getInventoryCountHistory(50);
      setHistory(rows || []);
    } catch (err: any) {
      showToast(err.message || "Failed to load count history", "err");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "usage") {
      loadUsageData();
    } else if (activeTab === "history") {
      loadHistoryData();
    }
  }, [activeTab, usageTypeFilter, usageVelocityFilter, usageSort]);

  // Handle Usage Search debounce
  useEffect(() => {
    if (activeTab !== "usage") return;
    const timer = setTimeout(() => {
      loadUsageData();
    }, 250);
    return () => clearTimeout(timer);
  }, [usageSearch]);

  // Start Count Session
  const handleStartCount = async (e: FormEvent) => {
    e.preventDefault();
    setStartingCount(true);
    try {
      const sessionId = await startInventoryCount(countType, countNotes.trim() || null);
      setIsStartModalOpen(false);
      setCountNotes("");
      setActiveSessionId(sessionId);
      setActiveTab("active_count");
      showToast("✓ Count session initialized");
      await loadSessionItems(sessionId);
    } catch (err: any) {
      showToast(err.message || "Failed to start count", "err");
    } finally {
      setStartingCount(false);
    }
  };

  // Load Active Session Items
  const loadSessionItems = async (sessionId: string) => {
    setLoading(true);
    try {
      const items = await getCountItems(
        sessionId,
        countSearch.trim() || null,
        countTypeFilter !== "all" ? countTypeFilter : null
      );
      setCountItems(items || []);
    } catch (err: any) {
      showToast(err.message || "Failed to load count items", "err");
    } finally {
      setLoading(false);
    }
  };

  // Save Item Count
  const handleSaveItem = async (menuItemId: string, full: number, partialPct: number) => {
    if (!activeSessionId) return;
    try {
      await recordItemCount({
        session_id: activeSessionId,
        menu_item_id: menuItemId,
        count_full: full,
        count_partial_pct: partialPct,
      });
      // Optimistic update
      setCountItems((prev) =>
        prev.map((item) =>
          item.menu_item_id === menuItemId
            ? { ...item, already_counted: true, count_full: full, count_partial_pct: partialPct }
            : item
        )
      );
      showToast("Saved count");
    } catch (err: any) {
      showToast(err.message || "Save error", "err");
    }
  };

  const [scannerOpen, setScannerOpen] = useState(false);

  const handleScanCountBarcode = (barcode: string) => {
    setScannerOpen(false);
    const clean = barcode.trim().toLowerCase();
    const match = countItems.find(
      (item) =>
        (item.upc && item.upc.toLowerCase() === clean) ||
        (item.sku && item.sku.toLowerCase() === clean) ||
        item.name.toLowerCase().includes(clean)
    );
    if (match) {
      const currentFull = match.count_full || 0;
      const nextFull = currentFull + 1;
      handleSaveItem(match.menu_item_id, nextFull, match.count_partial_pct || 0);
      showToast(`✓ Scanned "${match.name}": Count +1 (Total: ${nextFull})`);
      const rowEl = document.getElementById(`count-row-${match.menu_item_id}`);
      if (rowEl) {
        rowEl.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    } else {
      showToast(`No item in current session matching barcode: ${barcode}`, "err");
    }
  };

  // Complete Count
  const handleCompleteCount = async () => {
    if (!activeSessionId) return;
    const counted = countItems.filter((i) => i.already_counted).length;
    if (counted === 0) {
      showToast("Count at least one item before completing", "err");
      return;
    }
    if (!window.confirm(`Complete inventory count with ${counted} items recorded?`)) return;

    setCompletingCount(true);
    try {
      const res = await completeInventoryCount(activeSessionId);
      showToast(`✓ Count complete! Variance: ${res.total_variance_units} units (${fmtCurrency(res.total_variance_cost_cents)})`);
      setActiveSessionId(null);
      setActiveTab("history");
      await loadHistoryData();
    } catch (err: any) {
      showToast(err.message || "Failed to complete count", "err");
    } finally {
      setCompletingCount(false);
    }
  };

  // View Variance
  const handleViewVariance = async (sessionId: string, status: string) => {
    if (status === "in_progress") {
      setActiveSessionId(sessionId);
      setActiveTab("active_count");
      await loadSessionItems(sessionId);
      return;
    }
    setSelectedSessionId(sessionId);
    setActiveTab("variance");
    setLoadingVariance(true);
    try {
      const rows = await getCountVariance(sessionId);
      setVarianceRows(rows || []);
    } catch (err: any) {
      showToast(err.message || "Failed to load variance breakdown", "err");
    } finally {
      setLoadingVariance(false);
    }
  };

  // Filtered Active Items
  const filteredCountItems = useMemo(() => {
    let list = countItems;
    if (showOnlyUncounted) {
      list = list.filter((i) => !i.already_counted);
    }
    if (countSearch.trim()) {
      const q = countSearch.toLowerCase().trim();
      list = list.filter((i) => i.name.toLowerCase().includes(q) || (i.category && i.category.toLowerCase().includes(q)));
    }
    return list;
  }, [countItems, showOnlyUncounted, countSearch]);

  const countedCount = countItems.filter((i) => i.already_counted).length;

  return (
    <div style={styles.container}>
      <WhiskeyStudioTabs activeTab="cycleCount" badgeCounts={{ cycleCount: history.length }} />
      {toast && (
        <div style={{ ...styles.toast, background: toast.type === "err" ? "#E05040" : "var(--gold)" }}>
          {toast.text}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Inventory Intelligence &amp; Cycle Count</h1>
          <p style={styles.subtitle}>
            Monitor inventory velocity, days on hand, physical cycle counts, and POS depletions.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {activeSessionId && activeTab !== "active_count" && (
            <button
              type="button"
              style={styles.btnGold}
              onClick={() => setActiveTab("active_count")}
            >
              Resume Active Count
            </button>
          )}
          <button
            type="button"
            style={styles.btnPrimary}
            onClick={() => setIsStartModalOpen(true)}
          >
            + Start Count
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={styles.tabsRow}>
        <button
          type="button"
          style={activeTab === "usage" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("usage")}
        >
          Usage &amp; Movement
        </button>
        <button
          type="button"
          style={activeTab === "history" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("history")}
        >
          Count History
        </button>
        {activeSessionId && (
          <button
            type="button"
            style={activeTab === "active_count" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("active_count")}
          >
            Active Count ({countedCount}/{countItems.length})
          </button>
        )}
        {selectedSessionId && activeTab === "variance" && (
          <button type="button" style={styles.tabActive}>
            Variance Analysis
          </button>
        )}
      </div>

      {/* ── Tab 1: Usage Intelligence ────────────────────────────────────────── */}
      {activeTab === "usage" && (
        <div>
          {/* KPI Dashboard */}
          {kpis && (
            <div style={styles.kpiGrid}>
              <div style={styles.kpiCard}>
                <div style={styles.kpiValue}>{kpis.total_items ?? usageItems.length}</div>
                <div style={styles.kpiLabel}>Total Monitored Items</div>
              </div>
              <div style={styles.kpiCard}>
                <div style={styles.kpiValue}>{fmtCurrency(kpis.total_value_cents)}</div>
                <div style={styles.kpiLabel}>Inventory Asset Value</div>
              </div>
              <div style={styles.kpiCard}>
                <div style={{ ...styles.kpiValue, color: "#27ae60" }}>
                  {kpis.fast_velocity_count ?? "—"}
                </div>
                <div style={styles.kpiLabel}>Fast Moving (&gt;15 units/mo)</div>
              </div>
              <div style={styles.kpiCard}>
                <div style={{ ...styles.kpiValue, color: "var(--gold)" }}>
                  {kpis.moderate_velocity_count ?? "—"}
                </div>
                <div style={styles.kpiLabel}>Moderate (5–15 units/mo)</div>
              </div>
              <div style={styles.kpiCard}>
                <div style={{ ...styles.kpiValue, color: "#e67e22" }}>
                  {kpis.slow_velocity_count ?? "—"}
                </div>
                <div style={styles.kpiLabel}>Slow Moving (1–5 units/mo)</div>
              </div>
              <div style={styles.kpiCard}>
                <div style={{ ...styles.kpiValue, color: "var(--muted)" }}>
                  {kpis.dead_stock_count ?? "—"}
                </div>
                <div style={styles.kpiLabel}>Dead Stock (0 units 30d)</div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div style={styles.filterBar}>
            <input
              type="text"
              placeholder="Search spirits, bottles, categories…"
              value={usageSearch}
              onChange={(e) => setUsageSearch(e.target.value)}
              style={styles.searchInput}
            />
            <select
              value={usageVelocityFilter}
              onChange={(e) => setUsageVelocityFilter(e.target.value)}
              style={styles.select}
            >
              <option value="all">All Velocities</option>
              <option value="fast">Fast Moving</option>
              <option value="moderate">Moderate</option>
              <option value="slow">Slow Moving</option>
              <option value="dead">Dead Stock</option>
            </select>
            <select
              value={usageTypeFilter}
              onChange={(e) => setUsageTypeFilter(e.target.value)}
              style={styles.select}
            >
              <option value="all">All Types</option>
              <option value="bottle">Retail Bottles</option>
              <option value="pour">Bar Pours</option>
            </select>
            <select
              value={usageSort}
              onChange={(e) => setUsageSort(e.target.value)}
              style={styles.select}
            >
              <option value="units_30d">Sort by 30-Day Units</option>
              <option value="days_on_hand">Sort by Days on Hand</option>
              <option value="current_inventory">Sort by Current Stock</option>
              <option value="name">Sort by Name (A-Z)</option>
            </select>
          </div>

          {/* Table */}
          {loading ? (
            <div style={styles.loader}>Loading usage intelligence…</div>
          ) : usageItems.length === 0 ? (
            <div style={styles.emptyState}>No items match the selected filters.</div>
          ) : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Item &amp; Category</th>
                    <th style={styles.th}>Shelf Code</th>
                    <th style={styles.th}>Velocity</th>
                    <th style={styles.th}>Current Stock</th>
                    <th style={styles.th}>30-Day Depletion</th>
                    <th style={styles.th}>Est. Days on Hand</th>
                    <th style={styles.th}>Unit Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {usageItems.map((item) => {
                    const velStyle = VELOCITY_COLORS[item.velocity] || VELOCITY_COLORS.dead;
                    return (
                      <tr key={item.menu_item_id} style={styles.tr}>
                        <td style={styles.td}>
                          <div style={{ fontWeight: 700, color: "var(--text)" }}>{item.name}</div>
                          <div style={{ fontSize: 11, color: "var(--muted)" }}>
                            {item.item_type} {item.category ? `· ${item.category}` : ""}
                          </div>
                        </td>
                        <td style={styles.td}>
                          <span style={styles.codeBadge}>{item.shelf_code || "—"}</span>
                        </td>
                        <td style={styles.td}>
                          <span
                            style={{
                              ...styles.velBadge,
                              background: velStyle.bg,
                              color: velStyle.color,
                            }}
                          >
                            {(item.velocity || "low").toUpperCase()}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <strong>{item.current_inventory ?? 0}</strong>
                        </td>
                        <td style={styles.td}>
                          <span style={{ color: "var(--gold)", fontWeight: 700 }}>
                            {item.units_30d ?? 0}
                          </span>{" "}
                          units
                        </td>
                        <td style={styles.td}>
                          {item.days_on_hand != null ? (
                            <span
                              style={{
                                color: item.days_on_hand < 14 ? "#E05040" : "var(--text)",
                                fontWeight: item.days_on_hand < 14 ? 800 : 500,
                              }}
                            >
                              {item.days_on_hand} days
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td style={styles.td}>{fmtCurrency(item.cost_cents)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Tab 2: Count History ─────────────────────────────────────────────── */}
      {activeTab === "history" && (
        <div>
          {loading ? (
            <div style={styles.loader}>Loading count history…</div>
          ) : history.length === 0 ? (
            <div style={styles.emptyState}>
              No count sessions found. Click <strong>+ Start Count</strong> to begin.
            </div>
          ) : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date &amp; Time</th>
                    <th style={styles.th}>Counted By</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Items Counted</th>
                    <th style={styles.th}>Variance Units</th>
                    <th style={styles.th}>Variance Cost</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((row) => (
                    <tr key={row.id} style={styles.tr}>
                      <td style={styles.td}>
                        <strong>{fmtDate(row.started_at)}</strong>
                        {row.notes && <div style={{ fontSize: 11, color: "var(--muted)" }}>{row.notes}</div>}
                      </td>
                      <td style={styles.td}>{row.counted_by_name || "Staff"}</td>
                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.statusBadge,
                            background:
                              row.status === "completed"
                                ? "rgba(39,174,96,0.15)"
                                : row.status === "in_progress"
                                ? "rgba(249,175,54,0.15)"
                                : "rgba(138,122,102,0.15)",
                            color:
                              row.status === "completed"
                                ? "#27ae60"
                                : row.status === "in_progress"
                                ? "var(--gold)"
                                : "var(--muted)",
                          }}
                        >
                          {(row.status || "unknown").replace("_", " ").toUpperCase()}
                        </span>
                      </td>
                      <td style={styles.td}>{row.count_type}</td>
                      <td style={styles.td}>{row.items_counted || 0}</td>
                      <td style={styles.td}>
                        <span
                          style={{
                            color:
                              row.total_variance_units > 0
                                ? "#27ae60"
                                : row.total_variance_units < 0
                                ? "#E05040"
                                : "var(--muted)",
                            fontWeight: 700,
                          }}
                        >
                          {row.total_variance_units > 0 ? `+${row.total_variance_units}` : row.total_variance_units}
                        </span>
                      </td>
                      <td style={styles.td}>{fmtCurrency(row.total_variance_cost_cents)}</td>
                      <td style={styles.td}>
                        <button
                          type="button"
                          style={styles.btnSmall}
                          onClick={() => handleViewVariance(row.id, row.status)}
                        >
                          {row.status === "in_progress" ? "Resume Count →" : "View Breakdown"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Tab 3: Active Count Session ──────────────────────────────────────── */}
      {activeTab === "active_count" && activeSessionId && (
        <div>
          {/* Progress Bar & Controls */}
          <div style={styles.activeToolbar}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={styles.progressText}>
                <strong>{countedCount}</strong> of {countItems.length} items counted
              </div>
              <div style={styles.progressBarBg}>
                <div
                  style={{
                    ...styles.progressBarFill,
                    width: `${countItems.length > 0 ? (countedCount / countItems.length) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                type="button"
                style={{
                  background: "rgba(249,175,54,0.15)",
                  border: "1px solid var(--gold, #F9AF36)",
                  color: "var(--gold, #F9AF36)",
                  borderRadius: 8,
                  padding: "8px 14px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
                onClick={() => setScannerOpen(true)}
                title="Scan barcode to increment count"
              >
                📷 Scan Bottle
              </button>
              <button
                type="button"
                style={styles.btnGold}
                disabled={completingCount || countedCount === 0}
                onClick={handleCompleteCount}
              >
                {completingCount ? "Completing…" : `Complete Count (${countedCount})`}
              </button>
              <button
                type="button"
                style={styles.btnSecondary}
                onClick={() => {
                  if (window.confirm("Pause session and return to history? (Progress is saved)")) {
                    setActiveTab("history");
                  }
                }}
              >
                Pause &amp; Exit
              </button>
            </div>
          </div>

          {/* Search & Filter */}
          <div style={styles.filterBar}>
            <input
              type="text"
              placeholder="Search bottles to count…"
              value={countSearch}
              onChange={(e) => setCountSearch(e.target.value)}
              style={styles.searchInput}
            />
            <select
              value={countTypeFilter}
              onChange={(e) => setCountTypeFilter(e.target.value as any)}
              style={styles.select}
            >
              <option value="all">All Types</option>
              <option value="bottle">Retail Bottles</option>
              <option value="pour">Bar Pours</option>
            </select>
            <button
              type="button"
              style={showOnlyUncounted ? styles.filterBtnActive : styles.filterBtn}
              onClick={() => setShowOnlyUncounted(!showOnlyUncounted)}
            >
              {showOnlyUncounted ? "Showing Uncounted Only" : "Show All Items"}
            </button>
          </div>

          {/* Items list */}
          {loading ? (
            <div style={styles.loader}>Loading count items…</div>
          ) : filteredCountItems.length === 0 ? (
            <div style={styles.emptyState}>No items match the current search.</div>
          ) : (
            <div style={styles.countCardsList}>
              {filteredCountItems.map((item) => (
                <CountRowItem
                  key={item.menu_item_id}
                  item={item}
                  onSave={(full, pct) => handleSaveItem(item.menu_item_id, full, pct)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Tab 4: Variance Analysis ─────────────────────────────────────────── */}
      {activeTab === "variance" && selectedSessionId && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <button
              type="button"
              style={styles.btnSecondary}
              onClick={() => setActiveTab("history")}
            >
              ← Back to Count History
            </button>
          </div>

          {loadingVariance ? (
            <div style={styles.loader}>Calculating variance breakdown…</div>
          ) : varianceRows.length === 0 ? (
            <div style={styles.emptyState}>No variance entries recorded for this session.</div>
          ) : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Item &amp; Type</th>
                    <th style={styles.th}>Physical Count</th>
                    <th style={styles.th}>System Expected</th>
                    <th style={styles.th}>Variance Units</th>
                    <th style={styles.th}>Cost Impact</th>
                  </tr>
                </thead>
                <tbody>
                  {varianceRows.map((v) => {
                    const isOver = v.variance > 0;
                    const isUnder = v.variance < 0;
                    return (
                      <tr key={v.menu_item_id} style={styles.tr}>
                        <td style={styles.td}>
                          <strong>{v.name}</strong>
                          <div style={{ fontSize: 11, color: "var(--muted)" }}>{v.item_type}</div>
                        </td>
                        <td style={styles.td}>
                          {v.effective_count.toFixed(v.count_partial_pct ? 1 : 0)}
                          {v.count_partial_pct ? ` (+${v.count_partial_pct}%)` : ""}
                        </td>
                        <td style={styles.td}>{v.system_count ?? "—"}</td>
                        <td style={styles.td}>
                          <span
                            style={{
                              color: isOver ? "#27ae60" : isUnder ? "#E05040" : "var(--muted)",
                              fontWeight: 800,
                            }}
                          >
                            {isOver ? `+${v.variance.toFixed(1)}` : v.variance.toFixed(1)}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span
                            style={{
                              color: isUnder ? "#E05040" : isOver ? "#27ae60" : "var(--muted)",
                              fontWeight: 700,
                            }}
                          >
                            {fmtCurrency(v.variance_cost_cents)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Barcode Scanner Modal */}
      <BarcodeScannerModal
        isOpen={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={handleScanCountBarcode}
        title="Scan Bottle for Inventory Count"
      />

      {/* Start Count Modal */}
      {isStartModalOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <h2 style={styles.modalTitle}>Start New Inventory Count</h2>
            <form onSubmit={handleStartCount}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Count Type</label>
                <select
                  value={countType}
                  onChange={(e) => setCountType(e.target.value)}
                  style={styles.modalSelect}
                >
                  <option value="full">Full Bar &amp; Retail Count</option>
                  <option value="spot_check">Spot Check</option>
                  <option value="category">Category Count</option>
                </select>
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Staff Notes (Optional)</label>
                <textarea
                  rows={3}
                  placeholder="e.g. End of Month cycle audit, whiskey library section..."
                  value={countNotes}
                  onChange={(e) => setCountNotes(e.target.value)}
                  style={styles.modalTextarea}
                />
              </div>
              <div style={styles.modalActions}>
                <button
                  type="button"
                  style={styles.btnSecondary}
                  onClick={() => setIsStartModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={styles.btnGold}
                  disabled={startingCount}
                >
                  {startingCount ? "Starting…" : "Initialize Count Session"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function CountRowItem({
  item,
  onSave,
}: {
  item: CountItemRow;
  onSave: (full: number, partialPct: number) => void;
}) {
  const [full, setFull] = useState<string>(
    item.already_counted ? String(item.count_full) : ""
  );
  const [partial, setPartial] = useState<string>(
    item.already_counted && item.count_partial_pct ? String(item.count_partial_pct) : ""
  );

  useEffect(() => {
    if (item.already_counted) {
      setFull(String(item.count_full));
      setPartial(item.count_partial_pct ? String(item.count_partial_pct) : "");
    }
  }, [item.count_full, item.count_partial_pct, item.already_counted]);

  const handleSave = () => {
    const f = parseInt(full, 10) || 0;
    const p = Math.min(Math.max(parseInt(partial, 10) || 0, 0), 99);
    onSave(f, p);
  };

  return (
    <div
      id={`count-row-${item.menu_item_id}`}
      style={{
        ...styles.countCard,
        borderColor: item.already_counted ? "rgba(39,174,96,0.5)" : "var(--border)",
        background: item.already_counted ? "rgba(39,174,96,0.04)" : "var(--surface)",
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <strong style={{ color: "var(--text)", fontSize: 14 }}>{item.name}</strong>
          {item.already_counted && (
            <span style={styles.savedPill}>✓ Saved: {item.count_full}{item.count_partial_pct ? ` +${item.count_partial_pct}%` : ""}</span>
          )}
        </div>
        <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
          {item.item_type} {item.category ? `· ${item.category}` : ""} · System: <strong>{item.system_count ?? "0"}</strong>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: 10, color: "var(--muted)", textTransform: "uppercase" }}>Full</span>
          <input
            type="number"
            min="0"
            placeholder="0"
            value={full}
            onChange={(e) => setFull(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
            }}
            style={styles.countInput}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: 10, color: "var(--muted)", textTransform: "uppercase" }}>Open %</span>
          <input
            type="number"
            min="0"
            max="99"
            placeholder="0%"
            value={partial}
            onChange={(e) => setPartial(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
            }}
            style={styles.countInputSmall}
          />
        </div>
        <button type="button" style={styles.btnSmall} onClick={handleSave}>
          Save
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: { padding: "20px 24px", maxWidth: 1200, margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 700, margin: 0, color: "var(--text)" },
  subtitle: { fontSize: 13, color: "var(--muted)", margin: "4px 0 0" },
  tabsRow: { display: "flex", gap: 8, borderBottom: "1px solid var(--border)", paddingBottom: 8, marginBottom: 20 },
  tab: { background: "transparent", border: 0, color: "var(--muted)", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 },
  tabActive: { background: "rgba(249,175,54,0.15)", border: 0, color: "var(--gold)", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13 },
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 20 },
  kpiCard: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: 14, textAlign: "center" },
  kpiValue: { fontSize: 20, fontWeight: 800, color: "var(--text)" },
  kpiLabel: { fontSize: 11, color: "var(--muted)", marginTop: 4, textTransform: "uppercase", letterSpacing: 0.4 },
  filterBar: { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 },
  searchInput: { flex: 1, minWidth: 200, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  select: { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  filterBtn: { background: "transparent", border: "1px solid var(--border)", borderRadius: 8, color: "var(--muted)", padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  filterBtnActive: { background: "rgba(249,175,54,0.15)", border: "1px solid var(--gold)", borderRadius: 8, color: "var(--gold)", padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  // overflowX auto, not hidden: on phones the 5–8 column tables were clipped and unreachable.
  tableWrapper: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: 13 },
  th: { padding: "10px 14px", borderBottom: "1px solid var(--border)", color: "var(--muted)", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 },
  tr: { borderBottom: "1px solid var(--border)" },
  td: { padding: "12px 14px", color: "var(--text)" },
  velBadge: { fontSize: 10, fontWeight: 800, padding: "3px 8px", borderRadius: 6 },
  statusBadge: { fontSize: 10, fontWeight: 800, padding: "3px 8px", borderRadius: 6 },
  codeBadge: { background: "rgba(255,255,255,0.06)", padding: "2px 6px", borderRadius: 4, fontFamily: "monospace", fontSize: 11 },
  activeToolbar: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  progressText: { fontSize: 13, color: "var(--text)" },
  progressBarBg: { width: 140, height: 8, background: "var(--border)", borderRadius: 4, overflow: "hidden" },
  progressBarFill: { height: "100%", background: "var(--gold)", transition: "width 0.2s ease" },
  countCardsList: { display: "grid", gap: 8 },
  countCard: { border: "1px solid", borderRadius: 10, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 },
  countInput: { width: 64, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--text)", padding: "6px 8px", fontSize: 14, textAlign: "center", fontWeight: 700 },
  countInputSmall: { width: 64, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--text)", padding: "6px 8px", fontSize: 14, textAlign: "center" },
  savedPill: { fontSize: 10, fontWeight: 700, color: "#27ae60", background: "rgba(39,174,96,0.12)", padding: "2px 6px", borderRadius: 4 },
  btnPrimary: { background: "var(--gold)", color: "#111", border: 0, padding: "8px 16px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" },
  btnGold: { background: "var(--gold)", color: "#111", border: 0, padding: "8px 16px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" },
  btnSecondary: { background: "transparent", color: "var(--text)", border: "1px solid var(--border)", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" },
  btnSmall: { background: "transparent", color: "var(--gold)", border: "1px solid rgba(249,175,54,0.4)", padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" },
  loader: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 14 },
  emptyState: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 13 },
  toast: { position: "fixed", bottom: 20, right: 20, color: "#111", fontWeight: 700, padding: "10px 18px", borderRadius: 8, zIndex: 9999, fontSize: 13, boxShadow: "0 4px 12px rgba(0,0,0,0.4)" },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 },
  modalContent: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, width: "100%", maxWidth: 440, padding: 24 },
  modalTitle: { fontSize: 18, fontWeight: 700, color: "var(--text)", margin: "0 0 16px" },
  fieldGroup: { marginBottom: 16 },
  label: { display: "block", fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--muted)", marginBottom: 6, letterSpacing: 0.5 },
  modalSelect: { width: "100%", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "10px 12px", fontSize: 13 },
  modalTextarea: { width: "100%", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "10px 12px", fontSize: 13, resize: "vertical" },
  modalActions: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 },
};
